import React, { useEffect, useRef } from "react";
import { useSubscription } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import {
  updateCompanySyncLog,
  removeSyncingCompany,
  RemoteSyncLog,
} from "../slices/companiesSlice";
import {
  ON_COMPANY_REMOTE_SYNC_LOG_SUBSCRIPTION,
  CompanyRemoteSyncLogSubscriptionData,
} from "../graphql/subscriptions/onCompanyRemoteSyncLogSubscription";

interface CompanySyncSubscriptionProps {
  companyId: number;
}

/**
 * Component that subscribes to a single company's sync log and updates Redux.
 * Renders nothing - just handles the subscription side effect.
 */
const CompanySyncSubscription: React.FC<CompanySyncSubscriptionProps> = ({
  companyId,
}) => {
  const dispatch = useDispatch();
  const hasSeenInProgress = useRef(false);

  const { data } = useSubscription<CompanyRemoteSyncLogSubscriptionData>(
    ON_COMPANY_REMOTE_SYNC_LOG_SUBSCRIPTION,
    {
      variables: { companyId },
    }
  );

  useEffect(() => {
    if (data?.remote_sync_log?.[0]) {
      const log = data.remote_sync_log[0];

      const syncLog: RemoteSyncLog = {
        id: log.id,
        status: log.status as RemoteSyncLog["status"],
        log: log.log,
        syncResults: log.sync_results,
        startedAt: log.started_at,
        completedAt: log.completed_at,
      };

      // Update the company's sync log in Redux
      dispatch(updateCompanySyncLog({ companyId, syncLog }));

      // Track when we've seen the sync actually start
      if (log.status === "in_progress") {
        hasSeenInProgress.current = true;
      }

      // Only auto-remove after we've seen in_progress transition to success/failed
      // This prevents removing on stale data from a previous sync
      if (
        hasSeenInProgress.current &&
        (log.status === "success" || log.status === "failed")
      ) {
        dispatch(removeSyncingCompany(companyId));
      }
    }
  }, [data, companyId, dispatch]);

  return null;
};

/**
 * Component that renders subscription handlers for all syncing companies.
 * Place this in CompanyList to enable real-time sync status updates.
 */
export const CompanySyncSubscriptions: React.FC = () => {
  const syncingCompanyIds = useSelector(
    (state: RootState) => state.companies.syncingCompanyIds
  );

  return (
    <>
      {syncingCompanyIds.map((companyId) => (
        <CompanySyncSubscription key={companyId} companyId={companyId} />
      ))}
    </>
  );
};
